/**
 * The Locations screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/locations` (all optional; a default is never written):
 * - `q`: search on the location name and timezone.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends locations in no defined order, so
 *   the default is Name ascending.
 */
import type { V2Location } from '@/api/locations';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const LOCATIONS_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type LocationSortKey = 'name' | 'timezone' | 'status';
export const LOCATION_SORT_KEYS: readonly LocationSortKey[] = ['name', 'timezone', 'status'];
export const LOCATION_DEFAULT_SORT: SortState<LocationSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a location (its name, falling back to the id). */
export function locationName(location: V2Location): string {
  return location.name === '' ? location.id : location.name;
}

/** What a location row shows (and sorts by); a missing timezone sorts last. */
export function locationSortValue(location: V2Location, key: LocationSortKey): SortValue {
  switch (key) {
    case 'name':
      return locationName(location);
    case 'timezone':
      return location.timezone;
    case 'status':
      return location.isActive ? 'Active' : 'Inactive';
  }
}

/** The Locations search: the name or the timezone. */
export function locationMatches(location: V2Location, query: string): boolean {
  return matchesSearch(query, [locationName(location), location.timezone]);
}
